import { CompositeLayer } from '@deck.gl/core'
import { IconLayer, TextLayer } from '@deck.gl/layers'
import {
  BALLOON,
  BALLOON_BOTTOM_Y,
  BEAK,
  LABEL_PIXEL_OFFSET,
  LINE_HEIGHT,
  ROW_HEIGHT_SCALE,
  SHADOW,
  getDotOffset,
  getIconOffset,
  getIconSize,
  getIconSpaces,
  getLastRowCenterY,
  withIconSpaces,
} from '@/js/util/SpeechBalloonLabelUtil.js'

/**
 * 吹き出しラベルを「文字単位」で描くレイヤー
 *
 * 従来のラベルは1件ごとにラベル画像を作っていたため、表示する文字列が変わるたびに
 * 画像の作り直しとアトラスの詰め直しが発生する。
 * このレイヤーは文字ごとの画像を並べて描くので、**文字の種類が増えない限り
 * 文字列が変わっても作り直しが発生しない**。
 *
 * 見た目は共通ライブラリの `SpeechBalloonLabelGenerator`（従来のラベル画像）と揃えてある。
 * 従来のラベルは枠線を持たず、角丸の矩形に影を落とし、しっぽを角丸の曲線で描いている。
 * ここでは吹き出し本体を TextLayer の背景で、しっぽと影を重ねる形で再現している。
 */

/**
 * 透明（影として描くときの文字色）
 * @type {Array.<Number>}
 */
const TRANSPARENT = [0, 0, 0, 0]

/**
 * しっぽの画像を作るときの拡大率（5px程度で描くので粗さが出ないように）
 * @type {Number}
 */
const BEAK_IMAGE_SCALE = 8

/**
 * 文字幅キャッシュの上限（件）
 * 表示する値は更新のたびに変わるので、上限が無いと際限なく増える
 * @type {Number}
 */
const TEXT_WIDTH_CACHE_LIMIT = 4000

/**
 * アトラスに焼く文字の大きさの下限・上限
 *
 * 地図ライブラリの既定は64pxだが、画面には13〜14px程度で描くため、
 * そのままだと4倍以上に縮小されて線が痩せる（＝従来のラベル画像より甘くなる）。
 * 実際に画面へ描くピクセル数に合わせると縮小が起きなくなる
 * @type {Number}
 */
const ATLAS_FONT_SIZE_MIN = 8
const ATLAS_FONT_SIZE_MAX = 64

/**
 * 文字アトラスの拡大縮小のしかた
 *
 * 焼いた大きさと描く大きさを揃えていても、ラベルの位置は地図上の座標で決まるので
 * 画素の境目にはまらない。既定の補間（線形＋ミップ）だと、そのずれのぶんだけ
 * 隣の画素と混ざって文字が薄くなる。いちばん近い画素をそのまま使うようにすると、
 * 従来のラベル画像と同じ濃さになる（従来実装も `magFilter: 'nearest'` を指定している）
 * @type {Object}
 */
const ATLAS_TEXTURE_PARAMETERS = {
  magFilter: 'nearest',
  // 焼いた大きさより小さく描く地物（同じレイヤーに複数の文字サイズが混ざるとき）は
  // 画素を間引くとかえって線が欠けるので、縮小のときだけは混ぜる。
  // ミップ（縮小用の縮小画像）は等倍でも混ざってしまうので使わない
  minFilter: 'linear',
  mipmapFilter: 'nearest',
  addressModeU: 'clamp-to-edge',
  addressModeV: 'clamp-to-edge',
}

/**
 * しっぽの画像（初回だけ作る。ラベルの件数や文字列とは無関係）
 * @type {Object|null}
 */
let beakImage = null

/**
 * 色の丸の画像（初回だけ作る）
 * @type {Object|null}
 */
let dotImage = null

/**
 * 文字幅を測るための作業用コンテキスト
 * @type {CanvasRenderingContext2D|null}
 */
let measureContext = null

/**
 * 測った文字幅
 * @type {Map.<String, Number>}
 */
const textWidthCache = new Map()

/**
 * ラベルのフォント
 * 従来のラベル画像と同じく、画面のフォント設定をそのまま使う
 * @type {String|null}
 */
let fontFamily = null

/**
 * ラベルのフォントを返す
 * @returns {String} font-family
 */
export const getFontFamily = () => {
  if (fontFamily) return fontFamily
  const style = getComputedStyle(document.body, null)
  fontFamily = style.getPropertyValue('font-family') || 'sans-serif'
  return fontFamily
}

/**
 * 文字列の幅を測る（ラベルと同じフォント設定で）
 * @param {String} text 文字列
 * @param {String} font CSSのfont指定
 * @returns {Number} 幅(px)
 */
export const measureTextWidth = (text, font) => {
  const key = `${font} ${text}`
  const cached = textWidthCache.get(key)
  if (cached != null) return cached
  if (!measureContext) {
    measureContext = document.createElement('canvas').getContext('2d')
  }
  measureContext.font = font
  const width = measureContext.measureText(text).width
  if (textWidthCache.size >= TEXT_WIDTH_CACHE_LIMIT) textWidthCache.clear()
  textWidthCache.set(key, width)
  return width
}

/**
 * しっぽの画像を作る
 * 従来のラベル画像と同じ制御点で描く。白で描いておき、色は描画時に指定する
 * @returns {Object} { atlas, mapping }
 */
export const createBeakImage = () => {
  if (beakImage) return beakImage

  const width = BEAK.size * 2
  const height = BEAK.size
  const canvas = document.createElement('canvas')
  canvas.width = width * BEAK_IMAGE_SCALE
  canvas.height = height * BEAK_IMAGE_SCALE
  const context = canvas.getContext('2d')
  context.scale(BEAK_IMAGE_SCALE, BEAK_IMAGE_SCALE)
  context.fillStyle = '#ffffff'

  // 中央を基準に、上辺（吹き出しの下辺）から下向きに描く
  const center = BEAK.size
  const root = BEAK.rootRatio * BEAK.size
  const tip = BEAK.tipRatio * BEAK.size
  context.beginPath()
  context.moveTo(center + BEAK.size, 0)
  context.bezierCurveTo(
    center + BEAK.size - root,
    0,
    center + tip,
    BEAK.size,
    center,
    BEAK.size
  )
  context.bezierCurveTo(
    center - tip,
    BEAK.size,
    center - BEAK.size + root,
    0,
    center - BEAK.size,
    0
  )
  context.closePath()
  context.fill()

  beakImage = {
    atlas: canvas,
    mapping: {
      beak: {
        x: 0,
        y: 0,
        width: canvas.width,
        height: canvas.height,
        anchorX: canvas.width / 2,
        anchorY: 0,
        // 描画時の色指定(getColor)を効かせるために必要
        mask: true,
      },
    },
  }
  return beakImage
}

/**
 * 色の丸の画像を作る
 * 白で描いておき、色は描画時に指定する
 * @returns {Object} { atlas, mapping }
 */
export const createDotImage = () => {
  if (dotImage) return dotImage
  const size = 64
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const context = canvas.getContext('2d')
  context.fillStyle = '#ffffff'
  context.beginPath()
  context.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2)
  context.fill()

  dotImage = {
    atlas: canvas,
    mapping: {
      dot: {
        x: 0,
        y: 0,
        width: size,
        height: size,
        // 描画時の色指定(getColor)を効かせるために必要
        mask: true,
      },
    },
  }
  return dotImage
}

// ラベル単位の前後関係を深度バッファで付けるための値（IFIELD_V4-7151）
//
// deck.gl は要素ごと（影→吹き出し→文字→アイコン→丸）にサブレイヤーを分けて描くため、
// そのままでは「全ラベルの背景を描いてから全ラベルの文字を描く」順になり、
// 重なったときに奥のラベルの文字が手前のラベルの上に出てしまう。
// ラベルごとに z を持たせて深度テストを効かせることで、ラベル単位の前後関係を作る。
//
// z は深度だけでなく投影にも使われるため、広げると画面上の位置が外側へずれる。
// 刻みを「画面の1ピクセルに相当する距離」を基準にすると、このずれも深度差も
// ズームによらず一定になる（引くと1ピクセルの実距離が伸び、寄ると縮むため）。
//
// 実測（2026-08-30・ローカル）:
//   - ずれ量は 中心からの距離px × zのピクセル換算 × 7.3e-4
//     → 段の総数255・刻み0.003px相当なら、2560×1440 の隅でも 0.9px 以内
//   - 深度差は 1ピクセル換算あたり 6.7e-4。刻み0.003px相当で段あたり 2.0e-6
//     深度バッファは24bit（分解能 6e-8）なので30倍以上の余裕がある
//   - 同じラベルの中の要素は同じ z に置き、深度の比較を less-equal にして描いた順で決める
//   - 段は重なるラベル同士だけ増える。86件が密集した画面で実測61段
const DEPTH = {
  // 段ひとつぶんの z（画面の何ピクセルに相当する距離か）
  stepInPixels: 0.003,
  // 段の上限。これを超えて重なっているところは同じ段になり、そこは混ざったままになる
  maxRank: 127,
}

/**
 * ラベルの種類ごとの段の下駄
 *
 * ユーザーのラベルとプレイスのラベルは別々のレイヤーで、段はそれぞれ独立に振られる。
 * 下駄を分けておかないと、両者が重なったときの前後が描画順まかせになる。
 * ユーザーのラベルを手前に置く
 * @type {Object}
 */
export const DEPTH_GROUP = {
  place: 0,
  user: DEPTH.maxRank + 1,
}

/**
 * 画面上の矩形どうしが重なっているか
 * @param {Object} a 矩形
 * @param {Object} b 矩形
 * @returns {Boolean} 重なっていればtrue
 */
const isOverlapped = (a, b) =>
  a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom

const defaultProps = {
  getPosition: { type: 'accessor', value: (d) => d.position },
  // ラベルの種類ごとの段の下駄。DEPTH_GROUP を渡す
  depthBaseRank: { type: 'number', value: DEPTH_GROUP.place },
  getText: { type: 'accessor', value: (d) => d.text },
  getSize: { type: 'accessor', value: 13 },
  getColor: { type: 'accessor', value: [51, 51, 51, 255] },
  getBackgroundColor: { type: 'accessor', value: [255, 255, 255, 255] },
  getIconColor: { type: 'accessor', value: [51, 51, 51, 255] },
  getIcon: { type: 'accessor', value: null },
  iconAtlas: { type: 'object', value: null },
  iconMapping: { type: 'object', value: null },
  // 2段目の先頭アイコンの大きさ。null なら文字サイズに追従する（getIconSize）
  iconSize: { type: 'accessor', value: null },
  iconGap: { type: 'number', value: 3 },
  lineHeight: { type: 'number', value: LINE_HEIGHT },
  // マーカーの上に出すなど、ラベル全体をずらしたいときに足すずらし量
  // ずらす量が地物ごとに変わる場合（プレイスはマップレイヤーのマーカーの大きさで変わる）は
  // 関数を渡せる
  basePixelOffset: { type: 'accessor', value: [0, 0] },
  characterSet: { type: 'object', value: 'auto' },
  tail: { type: 'boolean', value: true },
  shadow: { type: 'boolean', value: true },
  // アトラスに焼く文字の大きさ。画面に描く最大のピクセル数を渡す
  // （地物ごとに文字サイズが変わる場合は、その最大値）
  maxFontSize: { type: 'number', value: null },
  // 値の後ろに出す色の丸（ステイタスの色）。null なら出さない
  getDotColor: { type: 'accessor', value: null },
  // 色の丸が背景に埋もれるときの枠線の色
  getDotBorderColor: { type: 'accessor', value: null },
  dotSize: { type: 'number', value: 10 },
  dotGap: { type: 'number', value: 4 },
}

/**
 * 吹き出しラベルレイヤー
 */
class SpeechBalloonLabelLayer extends CompositeLayer {
  /**
   * 地物のラベル全体のずらし量を返す
   * @param {Object} d 描画データ
   * @param {Object} info アクセサ情報
   * @returns {Array.<Number>} ずらし量
   */
  _baseOffsetOf(d, info) {
    const base = this.props.basePixelOffset
    return (typeof base === 'function' ? base(d, info) : base) || [0, 0]
  }

  /**
   * ラベル全体のずらし量を足した位置を返す
   * @param {Number} offsetY 縦のずらし量
   * @returns {Array.<Number>|Function} ずらし量
   */
  _offset(offsetY) {
    const base = this.props.basePixelOffset
    if (typeof base !== 'function') {
      return [LABEL_PIXEL_OFFSET[0] + base[0], offsetY + base[1]]
    }
    return (d, info) => {
      const [x, y] = this._baseOffsetOf(d, info)
      return [LABEL_PIXEL_OFFSET[0] + x, offsetY + y]
    }
  }

  /**
   * 地物の文字サイズを返す
   * @param {Object} d 描画データ
   * @param {Object} info アクセサ情報
   * @returns {Number} 文字サイズ(px)
   */
  _fontSizeOf(d, info) {
    const { getSize } = this.props
    return typeof getSize === 'function' ? getSize(d, info) : getSize
  }

  /**
   * 地物のフォント指定を返す
   * @param {Object} d 描画データ
   * @param {Object} info アクセサ情報
   * @returns {String} CSSのfont指定
   */
  _fontOf(d, info) {
    return `${this._fontSizeOf(d, info)}px ${getFontFamily()}`
  }

  /**
   * 地図のキャンバスが、CSSの1ピクセルを実際に何ピクセルで描いているかを返す
   *
   * **画面の解像度（`window.devicePixelRatio`）とは一致しない。**
   * Googleマップでは地図ライブラリが実解像度で描く指定を打ち消してしまうため、
   * 高精細ディスプレイでもキャンバスは等倍で、表示するときにブラウザが引き伸ばしている。
   * ここで画面の解像度を使うと、実際に描く大きさの倍の文字を焼いてしまい、
   * 縮小のぶんだけかえって甘くなる
   * @returns {Number} キャンバスの倍率
   */
  _measuredCanvasPixelRatio() {
    const canvas = this.context?.device?.canvasContext?.canvas
    // 地図がまだ画面に出ていないときは幅が取れない
    if (canvas && canvas.clientWidth > 0 && canvas.width > 0) {
      return canvas.width / canvas.clientWidth
    }
    return null
  }

  /**
   * キャンバスの倍率。まだ測れないときは画面の解像度で暫定的に見積もる
   * @returns {Number} キャンバスの倍率
   */
  _canvasPixelRatio() {
    return this._measuredCanvasPixelRatio() ?? window.devicePixelRatio ?? 1
  }

  /**
   * アトラスに焼きたい文字の大きさを求める
   * @returns {Number} 文字の大きさ(px)
   */
  _wantedFontSize() {
    const { maxFontSize, getSize } = this.props
    const base =
      maxFontSize ??
      (typeof getSize === 'number' ? getSize : defaultProps.getSize.value)
    const ratio = this._canvasPixelRatio()
    return Math.min(
      ATLAS_FONT_SIZE_MAX,
      Math.max(ATLAS_FONT_SIZE_MIN, Math.ceil(base * ratio))
    )
  }

  /**
   * アトラスに焼く文字の大きさを返す
   *
   * 地図ライブラリは文字の寸法をこの値で割って正規化するため、
   * ここを変えても**文字送り・行の高さ・吹き出しの大きさは変わらない**。
   * 変わるのは画面に描くときの拡大縮小率だけで、画面上の実ピクセル数に
   * 合わせると縮小が起きなくなり、従来のラベル画像と同じ鮮明さに近づく
   * @returns {Object} fontSettings
   */
  _fontSettings() {
    return { fontSize: this.state.atlasFontSize ?? this._wantedFontSize() }
  }

  /**
   * 初期化
   */
  initializeState() {
    super.initializeState()
    this.setState({
      atlasFontSize: this._wantedFontSize(),
      atlasRatioMeasured: this._measuredCanvasPixelRatio() != null,
    })
  }

  /**
   * 状態の更新
   *
   * アトラスは焼く文字の大きさが変わるたびに作り直される。
   * 画面に出ている地物の最大サイズをそのまま使うと、地図を動かすたびに
   * 12px と 13px を行き来して**そのつど全文字を焼き直す**ことになるため、
   * 一度大きくしたら小さくしない
   * @param {Object} params 更新情報
   */
  updateState(params) {
    super.updateState(params)
    // レイヤーは地図が画面に出る前の1フレームで作られるため、そこではキャンバスの
    // 倍率が測れず、画面の解像度で暫定的に焼いてしまう（Googleマップでは2倍になる）。
    // 測れるようになった最初の1回だけは、暫定値より小さくても焼き直す
    const wanted = this._wantedFontSize()
    const measured = this._measuredCanvasPixelRatio() != null
    if (
      wanted > this.state.atlasFontSize ||
      (measured && !this.state.atlasRatioMeasured)
    ) {
      this.setState({ atlasFontSize: wanted, atlasRatioMeasured: measured })
    }
    // 段は画面上の重なりで決まるので、データだけでなく地図を動かしたときも作り直す。
    // ただし平行移動（パン）ではラベル同士の位置関係が変わらないので、
    // 縮尺・向き・傾きが変わったときとデータが変わったときだけにする
    const viewKey = this._depthViewKey()
    const wantsUpdate =
      params.changeFlags.dataChanged ||
      viewKey !== this.state.depthViewKey ||
      this.state.depthPending
    if (!wantsUpdate) return
    if (this._canProjectDepth()) {
      this.setState({ depthViewKey: viewKey, depthPending: false })
      this._updateDepthRanks()
    } else if (!this.state.depthPending) {
      // 使えるビューポートが来てから作り直す
      this.setState({ depthPending: true })
    }
  }

  /**
   * 段の計算に使えるビューポートかどうか
   *
   * Google 地図の WebGL 経路では、deck を作った直後の1フレームだけ
   * 緯度経度を持たない既定のビューポートで描画が走る。そのまま投影すると
   * NaN になり @math.gl/web-mercator の assertion でレイヤーの初期化が落ちるため、
   * 使えるビューポートが来るまで段の計算を見送る
   * @returns {Boolean} 投影できるか
   */
  _canProjectDepth() {
    const viewport = this.context.viewport
    return (
      !!viewport &&
      !!viewport.isGeospatial &&
      Number.isFinite(viewport.longitude) &&
      Number.isFinite(viewport.latitude)
    )
  }

  /**
   * ラベルの前後関係の段を作り直す
   *
   * 段が変わったときだけ状態を更新する。地図を動かしても段が変わらないことは多く、
   * 毎回更新すると位置の再計算が走ってしまうため
   */
  _updateDepthRanks() {
    const step = this._depthStep()
    const ranks = this._buildDepthRanks()
    const current = this.state.depthRanks
    const changed =
      step !== this.state.depthStep ||
      !current ||
      current.length !== ranks.length ||
      ranks.some((rank, index) => rank !== current[index])
    if (!changed) return
    this.setState({
      depthRanks: ranks,
      depthStep: step,
      depthVersion: (this.state.depthVersion || 0) + 1,
    })
  }

  /**
   * 段の作り直しが要るかを判ずるための、地図の見え方の指紋
   * @returns {String} 指紋
   */
  _depthViewKey() {
    const { zoom, pitch, bearing } = this.context.viewport
    return `${zoom}/${pitch}/${bearing}`
  }

  /**
   * 段ひとつぶんの z を求める
   *
   * 画面の1ピクセルに相当する距離を基準にすることで、
   * 位置のずれも深度差もズームによらず一定になる
   * @returns {Number} z(m)
   */
  _depthStep() {
    const viewport = this.context.viewport
    const { longitude, latitude } = viewport
    const delta = 0.001
    const [, y0] = viewport.project([longitude, latitude])
    const [, y1] = viewport.project([longitude, latitude + delta])
    const pixels = Math.abs(y1 - y0)
    if (!pixels) return 0
    // 緯度1度あたりのおおよその距離(m)から、1ピクセルの実距離を出す
    const metersPerPixel = (delta * 111320) / pixels
    return metersPerPixel * DEPTH.stepInPixels
  }

  /**
   * 画面上でのラベルの矩形を求める
   *
   * 吹き出しは文字の中央そろえ・下そろえで、そこに余白ぶんが付く
   * @param {Object} d 描画データ
   * @param {Number} index データの並び順
   * @returns {Object|null} 画面上の矩形
   */
  _labelBoxOf(d, index) {
    const { data, getPosition, getText, getSize } = this.props
    const info = { index, data }
    const position = getPosition(d, info)
    if (!position) return null
    const [x, y] = this.context.viewport.project(position)
    const fontSize = typeof getSize === 'function' ? getSize(d, info) : getSize
    const font = this._fontOf(d, info)
    const rows = String(
      withIconSpaces(
        getText(d, info),
        this._spacesOf(d, info).count,
        this._dotSpacesOf(d, info).count
      ) ?? ''
    ).split('\n')
    const width =
      Math.max(...rows.map((row) => measureTextWidth(row, font))) +
      BALLOON.padding[0] +
      BALLOON.padding[2]
    const height =
      rows.length * fontSize * ROW_HEIGHT_SCALE +
      BALLOON.padding[1] +
      BALLOON.padding[3]
    const offset = this._offset(LABEL_PIXEL_OFFSET[1])
    const [offsetX, offsetY] =
      typeof offset === 'function' ? offset(d, info) : offset
    const bottom = y + offsetY + BALLOON.padding[3]
    return {
      left: x + offsetX - width / 2,
      right: x + offsetX + width / 2,
      top: bottom - height,
      bottom,
    }
  }

  /**
   * ラベルの前後関係の段を決める
   *
   * 画面上で重なるラベル同士だけ段を分け、重ならないものは同じ段に置く。
   * こうすると z の総幅が小さいままになり、投影のずれを1px以内に抑えられる。
   * 南（画面の下）にあるものほど手前に出す。しっぽが下向きで、
   * 下にあるラベルが手前にあるように見えるため
   * @returns {Array.<Number>} データの並び順に対応する段
   */
  _buildDepthRanks() {
    const { data } = this.props
    const boxes = Array.from(data || [], (d, index) =>
      this._labelBoxOf(d, index)
    )
    // 画面の上にあるものから順に段を決める
    const order = boxes
      .map((box, index) => ({ index, box }))
      .filter((row) => row.box)
      .sort((a, b) => a.box.top - b.box.top)
    const ranks = new Array(boxes.length).fill(0)
    const placed = []
    for (const row of order) {
      let rank = 0
      for (const other of placed) {
        // 上から順に見ているので、下端が今の上端より上にあるものはもう重ならない
        if (other.box.bottom < row.box.top) continue
        if (!isOverlapped(row.box, other.box)) continue
        rank = Math.max(rank, ranks[other.index] + 1)
      }
      ranks[row.index] = Math.min(rank, DEPTH.maxRank)
      placed.push(row)
    }
    return ranks
  }

  /**
   * 要素ごとの z を足した位置アクセサを返す
   * @param {Function} getPosition もとの位置アクセサ
   * @param {Number} elementIndex 同じラベルの中での描画順（0が最背面）
   * @returns {Function} 位置アクセサ
   */
  _depthPosition(getPosition) {
    return (d, info) => {
      const position = getPosition(d, info)
      if (!position) return position
      const rank = this.state.depthRanks?.[info?.index] || 0
      const step = this.state.depthStep || 0
      return [
        position[0],
        position[1],
        (this.props.depthBaseRank + rank) * step,
      ]
    }
  }

  /**
   * 地物のアイコン名を返す
   * 表示項目によってはアイコンを持たないため、地物ごとに判定する
   * @param {Object} d 描画データ
   * @param {Object} info アクセサ情報
   * @returns {String|null} アイコン名
   */
  _iconOf(d, info) {
    const { getIcon, iconAtlas, iconMapping } = this.props
    // 画像が揃っていないとアイコンは描かれない。
    // ここで null を返さないと、アイコンぶんの空白だけが空いたラベルになる
    if (!getIcon || !iconAtlas || !iconMapping) return null
    return (typeof getIcon === 'function' ? getIcon(d, info) : getIcon) || null
  }

  /**
   * 地物の色の丸の色を返す
   * @param {Object} d 描画データ
   * @param {Object} info アクセサ情報
   * @returns {Array.<Number>|null} 色
   */
  _dotOf(d, info) {
    const { getDotColor } = this.props
    if (!getDotColor) return null
    return (
      (typeof getDotColor === 'function'
        ? getDotColor(d, info)
        : getDotColor) || null
    )
  }

  /**
   * 地物の色の丸の枠線の色を返す
   * 枠線が要らない場合（背景と見分けがつく色）は null
   * @param {Object} d 描画データ
   * @param {Object} info アクセサ情報
   * @returns {Array.<Number>|null} 色
   */
  _dotBorderOf(d, info) {
    const { getDotBorderColor } = this.props
    if (!getDotBorderColor || !this._dotOf(d, info)) return null
    return (
      (typeof getDotBorderColor === 'function'
        ? getDotBorderColor(d, info)
        : getDotBorderColor) || null
    )
  }

  /**
   * 値の行の末尾に、色の丸のぶんとして空ける空白を返す
   * @param {Object} d 描画データ
   * @param {Object} info アクセサ情報
   * @returns {Object} { count, width }
   */
  _dotSpacesOf(d, info) {
    const { dotSize, dotGap } = this.props
    if (!this._dotOf(d, info)) return { count: 0, width: 0 }
    const font = this._fontOf(d, info)
    return getIconSpaces(measureTextWidth(' ', font), dotSize, dotGap)
  }

  /**
   * 2段目のアイコンぶんに空ける空白を返す
   * アイコンを持たない地物には空白を空けない
   * @param {Object} d 描画データ
   * @param {Object} info アクセサ情報
   * @returns {Object} { count, width }
   */
  _spacesOf(d, info) {
    const { iconGap } = this.props
    if (!this._iconOf(d, info)) return { count: 0, width: 0 }
    const font = this._fontOf(d, info)
    return getIconSpaces(
      measureTextWidth(' ', font),
      this._iconSizeOf(d, info),
      iconGap
    )
  }

  /**
   * 2段目のアイコンの大きさを返す
   * 指定が無ければ文字サイズに追従させる（行の高さに収めて1段目に接しないようにする）
   * @param {Object} d 描画データ
   * @param {Object} info アクセサ情報
   * @returns {Number} アイコンの大きさ(px)
   */
  _iconSizeOf(d, info) {
    const { iconSize } = this.props
    if (typeof iconSize === 'function') return iconSize(d, info)
    if (iconSize != null) return iconSize
    return getIconSize(this._fontSizeOf(d, info))
  }

  /**
   * 吹き出し本体（と影）を描くレイヤーを作る
   * @param {String} id レイヤーID
   * @param {Number} offsetY 縦のずらし量
   * @param {Boolean} isShadow 影として描く場合はtrue
   * @returns {Object} レイヤー
   */
  _createBalloonLayer(id, offsetY, isShadow) {
    const {
      data,
      getPosition,
      getText,
      getSize,
      getColor,
      getBackgroundColor,
      characterSet,
      lineHeight,
      updateTriggers = {},
    } = this.props

    return new TextLayer(
      this.getSubLayerProps({
        id,
        data,
        billboard: true,
        pickable: false,
        // 文字ごとの画像を持つので、文字の種類が同じなら作り直しが起きない
        characterSet,
        fontFamily: getFontFamily(),
        // 画面に描くピクセル数でアトラスを焼く（縮小によるにじみを避ける）
        fontSettings: this._fontSettings(),
        // 文字を描くのは地図ライブラリの内側のレイヤーで、そこへは
        // テクスチャの指定が素通しされないため、名前を指定して差し込む
        _subLayerProps: {
          characters: { textureParameters: ATLAS_TEXTURE_PARAMETERS },
        },
        getText: (d, info) =>
          withIconSpaces(
            getText(d, info),
            this._spacesOf(d, info).count,
            this._dotSpacesOf(d, info).count
          ),
        getPosition: this._depthPosition(getPosition),
        getSize,
        getColor: isShadow ? TRANSPARENT : getColor,
        getPixelOffset: this._offset(offsetY),
        lineHeight,
        getTextAnchor: 'middle',
        getAlignmentBaseline: 'bottom',
        // 吹き出しの背景（角丸・余白）。従来のラベル画像と同じく枠線は無い
        background: true,
        getBackgroundColor: isShadow ? SHADOW.color : getBackgroundColor,
        getBorderWidth: 0,
        backgroundBorderRadius: BALLOON.borderRadius,
        backgroundPadding: BALLOON.padding,
        parameters: {
          depthTest: true,
          // 同じ段（＝同じラベルの中の要素）は描いた順が勝つようにする。
          // 段が違うところだけ深度で前後が決まる
          depthCompare: 'less-equal',
          depthRange: [0, 0.9],
          blend: true,
        },
        // deck.gl はサブレイヤーごとに深度をずらす（-layerIndex*100）。
        // その量が段の差より大きく、ラベル単位の前後を打ち消してしまうので止める
        getPolygonOffset: () => [0, 0],
        updateTriggers: {
          getPosition: this.state.depthVersion,
          getText: [updateTriggers.getText, updateTriggers.getIcon],
          getSize: updateTriggers.getSize,
          getColor: updateTriggers.getColor,
          getBackgroundColor: updateTriggers.getBackgroundColor,
        },
      })
    )
  }

  /**
   * しっぽを描くレイヤーを作る
   * @param {String} id レイヤーID
   * @param {Object} atlas しっぽの画像
   * @param {Number} offsetY 縦のずらし量
   * @param {Function|Array.<Number>} getColor 色
   * @returns {Object} レイヤー
   */
  _createBeakLayer(id, atlas, offsetY, getColor) {
    const { data, getPosition, updateTriggers = {} } = this.props
    return new IconLayer(
      this.getSubLayerProps({
        id,
        data,
        billboard: true,
        pickable: false,
        iconAtlas: atlas.atlas,
        iconMapping: atlas.mapping,
        getIcon: () => 'beak',
        getPosition: this._depthPosition(getPosition),
        getSize: BEAK.size,
        sizeUnits: 'pixels',
        getPixelOffset: this._offset(offsetY),
        getColor,
        parameters: {
          depthTest: true,
          // 同じ段（＝同じラベルの中の要素）は描いた順が勝つようにする。
          // 段が違うところだけ深度で前後が決まる
          depthCompare: 'less-equal',
          depthRange: [0, 0.9],
          blend: true,
        },
        // deck.gl はサブレイヤーごとに深度をずらす（-layerIndex*100）。
        // その量が段の差より大きく、ラベル単位の前後を打ち消してしまうので止める
        getPolygonOffset: () => [0, 0],
        updateTriggers: {
          getPosition: this.state.depthVersion,
          getColor: updateTriggers.getBackgroundColor,
        },
      })
    )
  }

  /**
   * 2段目のアイコンを描くレイヤーを作る
   * 文字の中に画像は混ぜられないため、別レイヤーで重ねる
   * @returns {Object} レイヤー
   */
  _createIconLayer() {
    const {
      data,
      getPosition,
      getText,
      getSize,
      getIcon,
      getIconColor,
      iconAtlas,
      iconMapping,
      lineHeight,
      updateTriggers = {},
    } = this.props

    return new IconLayer(
      this.getSubLayerProps({
        id: 'icon',
        data,
        billboard: true,
        pickable: false,
        iconAtlas,
        iconMapping,
        getIcon,
        getPosition: this._depthPosition(getPosition),
        getSize: (d, info) => this._iconSizeOf(d, info),
        sizeUnits: 'pixels',
        getPixelOffset: (d, info) => {
          const lines = `${getText(d, info) ?? ''}`.split('\n')
          const valueLine = lines[lines.length - 1] || ''
          const font = this._fontOf(d, info)
          const { width: spacesWidth } = this._spacesOf(d, info)
          // 末尾に色の丸のぶんの空白があると行全体が広がるので、それも数に入れる
          const { width: dotSpacesWidth } = this._dotSpacesOf(d, info)
          const fontSize =
            typeof getSize === 'function' ? getSize(d, info) : getSize
          const [x, y] = getIconOffset(
            spacesWidth,
            measureTextWidth(valueLine, font) + dotSpacesWidth,
            getLastRowCenterY(fontSize, lineHeight),
            this._iconSizeOf(d, info)
          )
          const base = this._baseOffsetOf(d, info)
          return [x + base[0], y + base[1]]
        },
        getColor: getIconColor,
        parameters: {
          depthTest: true,
          // 同じ段（＝同じラベルの中の要素）は描いた順が勝つようにする。
          // 段が違うところだけ深度で前後が決まる
          depthCompare: 'less-equal',
          depthRange: [0, 0.9],
          blend: true,
        },
        // deck.gl はサブレイヤーごとに深度をずらす（-layerIndex*100）。
        // その量が段の差より大きく、ラベル単位の前後を打ち消してしまうので止める
        getPolygonOffset: () => [0, 0],
        updateTriggers: {
          getPosition: this.state.depthVersion,
          getPixelOffset: [
            updateTriggers.getText,
            updateTriggers.getSize,
            updateTriggers.getIcon,
            updateTriggers.getDotColor,
          ],
          getSize: updateTriggers.getSize,
          getIcon: updateTriggers.getIcon,
          getColor: updateTriggers.getIconColor,
        },
      })
    )
  }

  /**
   * 値の後ろの色の丸を描くレイヤーを作る
   *
   * ステイタスの色は背景に近いと見えなくなるため、
   * 一回り大きい丸を枠線の色で先に敷いてから、本体の色を重ねる
   * @param {String} id レイヤーID
   * @param {Object} atlas 丸の画像
   * @param {Number} size 大きさ
   * @param {Function|Array.<Number>} getColor 色
   * @returns {Object} レイヤー
   */
  _createDotLayer(id, atlas, size, getColor, isBorder) {
    const {
      data,
      getPosition,
      getText,
      getSize,
      lineHeight,
      updateTriggers = {},
    } = this.props

    return new IconLayer(
      this.getSubLayerProps({
        id,
        data,
        billboard: true,
        pickable: false,
        iconAtlas: atlas.atlas,
        iconMapping: atlas.mapping,
        // 枠線は必要な地物にだけ描く（不要な地物に描くと丸が一回り大きく見える）
        getIcon: (d, info) =>
          isBorder
            ? this._dotBorderOf(d, info) && 'dot'
            : this._dotOf(d, info) && 'dot',
        getPosition: this._depthPosition(getPosition),
        // 枠線を描く地物は、本体を枠線のぶん内側に収める
        getSize: isBorder
          ? size
          : (d, info) => (this._dotBorderOf(d, info) ? size - 2 : size),
        sizeUnits: 'pixels',
        getPixelOffset: (d, info) => {
          const lines = `${getText(d, info) ?? ''}`.split('\n')
          const valueLine = lines[lines.length - 1] || ''
          const font = this._fontOf(d, info)
          const lead = this._spacesOf(d, info)
          const trail = this._dotSpacesOf(d, info)
          const lineWidth =
            lead.width + measureTextWidth(valueLine, font) + trail.width
          const fontSize =
            typeof getSize === 'function' ? getSize(d, info) : getSize
          const [x, y] = getDotOffset(
            trail.width,
            lineWidth,
            getLastRowCenterY(fontSize, lineHeight)
          )
          const base = this._baseOffsetOf(d, info)
          return [x + base[0], y + base[1]]
        },
        getColor,
        parameters: {
          depthTest: true,
          // 同じ段（＝同じラベルの中の要素）は描いた順が勝つようにする。
          // 段が違うところだけ深度で前後が決まる
          depthCompare: 'less-equal',
          depthRange: [0, 0.9],
          blend: true,
        },
        // deck.gl はサブレイヤーごとに深度をずらす（-layerIndex*100）。
        // その量が段の差より大きく、ラベル単位の前後を打ち消してしまうので止める
        getPolygonOffset: () => [0, 0],
        updateTriggers: {
          getPosition: this.state.depthVersion,
          getPixelOffset: [updateTriggers.getText, updateTriggers.getSize],
          getIcon: updateTriggers.getDotColor,
          getColor: [updateTriggers.getDotColor],
        },
      })
    )
  }

  /**
   * 描画
   * @returns {Array.<Object>} レイヤー
   */
  renderLayers() {
    const {
      getBackgroundColor,
      getIcon,
      iconAtlas,
      iconMapping,
      tail,
      shadow,
      getDotColor,
      getDotBorderColor,
      dotSize,
    } = this.props
    const beak = tail ? createBeakImage() : null
    const dot = getDotColor ? createDotImage() : null

    return [
      // 影。従来のラベル画像はぼかしが無いので、同じ形を下にずらして薄い黒で敷けば再現できる
      shadow && beak
        ? this._createBeakLayer(
            'shadow-beak',
            beak,
            BALLOON_BOTTOM_Y + SHADOW.offsetY,
            SHADOW.color
          )
        : null,
      shadow
        ? this._createBalloonLayer(
            'shadow',
            LABEL_PIXEL_OFFSET[1] + SHADOW.offsetY,
            true
          )
        : null,
      // しっぽ。吹き出し本体の「前」に描き、1px食い込ませて継ぎ目を無くす
      beak
        ? this._createBeakLayer(
            'beak',
            beak,
            BALLOON_BOTTOM_Y - 1,
            getBackgroundColor
          )
        : null,
      this._createBalloonLayer('balloon', LABEL_PIXEL_OFFSET[1], false),
      getIcon && iconAtlas && iconMapping ? this._createIconLayer() : null,
      // 枠線ぶんを一回り大きく敷いてから本体を重ねる
      dot && getDotBorderColor
        ? this._createDotLayer(
            'dot-border',
            dot,
            dotSize,
            getDotBorderColor,
            true
          )
        : null,
      dot
        ? this._createDotLayer('dot', dot, dotSize, getDotColor, false)
        : null,
    ].filter(Boolean)
  }
}

SpeechBalloonLabelLayer.layerName = 'SpeechBalloonLabelLayer'
SpeechBalloonLabelLayer.defaultProps = defaultProps

export default SpeechBalloonLabelLayer
